import { useCallback } from 'react';

const GENERIC_MEDIA_BODY =
  /^(📂|🎵|🎬|📷|📋)|arquivo de (documento|vídeo|áudio|imagem)/i;

/** Remove prefixo de timestamp do storage (ex.: 1730123456789-arquivo.cdr). */
export function stripStorageFilenamePrefix(name) {
  if (!name) return name;
  return String(name).replace(/^\d{10,}-/, "");
}

/** Nome para download/exibição: fileName da Meta, envio oficial, body ou mediaUrl. */
export function getDocumentDownloadFilename(message, fallback = "arquivo") {
  if (!message) return fallback;

  try {
    const parsed =
      typeof message.dataJson === "string"
        ? JSON.parse(message.dataJson)
        : message.dataJson;
    const metaName = parsed?.message?.fileName || parsed?.fileName;
    if (metaName && String(metaName).trim()) {
      return String(metaName).trim();
    }
  } catch (_) {
    /* ignore */
  }

  const body = message.body != null ? String(message.body).trim() : "";
  if (body && !GENERIC_MEDIA_BODY.test(body)) {
    const firstLine = body.split("\n")[0].trim();
    if (firstLine.includes(".")) return firstLine;
  }

  if (message.mediaUrl) {
    try {
      const base = stripStorageFilenamePrefix(
        decodeURIComponent(
          String(message.mediaUrl).split("/").pop().split("?")[0]
        )
      );
      if (base && base.includes(".")) return base;
    } catch (_) {
      /* ignore */
    }
  }

  if (body && !GENERIC_MEDIA_BODY.test(body)) return body;

  return fallback;
}

const AUDIO_FILE_EXT = /\.(ogg|oga|opus|mp3|m4a|aac|wav|weba|amr|3gp)$/i;
const VIDEO_FILE_EXT = /\.(mp4|webm|mov|avi|mkv|3gpp?2?)$/i;

export function isMediaAudioMessage(message) {
  if (!message) return false;
  const mt = String(message.mediaType || "").toLowerCase();
  if (mt === "audio" || mt === "audiomessage" || mt === "ptt" || mt === "voice") {
    return true;
  }
  const name = getDocumentDownloadFilename(message, "").toLowerCase();
  if (AUDIO_FILE_EXT.test(name)) return true;
  const url = String(message.mediaUrl || "").toLowerCase();
  if (AUDIO_FILE_EXT.test(url)) return true;
  const body = String(message.body || "").toLowerCase();
  return (
    body.includes("audio_") ||
    body.includes("áudio gravado") ||
    body.includes("arquivo de áudio") ||
    body.includes("mensagem de voz")
  );
}

export function isAttachmentDocumentMessage(message) {
  if (!message?.mediaUrl) return false;
  if (isMediaAudioMessage(message)) return false;

  const mt = String(message.mediaType || "").toLowerCase();
  if (mt === "video" || mt === "videomessage" || mt === "image" || mt === "sticker") {
    return false;
  }

  const name = getDocumentDownloadFilename(message, "").toLowerCase();
  if (AUDIO_FILE_EXT.test(name) || VIDEO_FILE_EXT.test(name)) return false;

  const url = String(message.mediaUrl).toLowerCase();
  if (AUDIO_FILE_EXT.test(url) || VIDEO_FILE_EXT.test(url)) return false;

  if (
    mt === "document" ||
    mt === "documentmessage" ||
    mt === "application"
  ) {
    return true;
  }

  return /\.[a-z0-9]{2,5}$/i.test(name) && !AUDIO_FILE_EXT.test(name);
}

export function isPdfUrl(mediaUrl, body, mediaType) {
  if (mediaType === "application/pdf") return true;

  const downloadName = body || "";
  const lowerName = downloadName.toLowerCase();
  if (lowerName.endsWith(".cdr") || lowerName.endsWith(".ai") || lowerName.endsWith(".psd")) {
    return false;
  }

  if (mediaUrl) {
    const url = mediaUrl.toLowerCase();
    if (url.endsWith(".cdr") || url.includes(".cdr?")) return false;
    return url.endsWith(".pdf") ||
      url.includes(".pdf?") ||
      url.includes("/pdf/");
  }

  if (body) {
    return lowerName.includes(".pdf");
  }

  return false;
}

export const usePdfViewer = () => {
  
  // Processar URL para garantir que esteja no formato correto
  const processUrl = useCallback((url) => {
    if (!url) return '';
    
    try {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        return url;
      }
      // Se a URL já tem protocolo, retornar como está
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      
      // Se começa com /public/, usar a URL base do backend
      if (url.startsWith('/public/')) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
        return `${backendUrl}${url}`;
      }
      
      // Se é apenas um nome de arquivo ou caminho relativo
      // ✅ CORRIGIDO: Extrair companyId da URL ou usar padrão
      // Para arquivos do fluxo, eles ficam em public/company{companyId}/ e não em public/company{companyId}/flow/
      if (!url.startsWith('/')) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
        // Se a URL contém "company" e um número, usar esse caminho
        if (url.includes('company') && url.match(/company\d+/)) {
          return `${backendUrl}/public/${url}`;
        }
        // Caso contrário, assumir que é apenas o nome do arquivo e precisa do companyId
        // Mas como não temos acesso ao companyId aqui, vamos tentar construir a URL
        // O backend deve salvar apenas o nome do arquivo, e aqui construímos o caminho completo
        // Por padrão, arquivos ficam em public/company{companyId}/, mas precisamos do companyId
        // Por enquanto, vamos assumir que se não começar com /, é apenas o nome do arquivo
        // e o componente que chama deve fornecer o caminho completo ou usar o padrão
        return `${backendUrl}/public/${url}`;
      }
      
      // Para outros casos, assumir que é um caminho absoluto do servidor
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
      return `${backendUrl}${url}`;
      
    } catch (error) {
      console.error('❌ Erro ao processar URL:', error);
      return url;
    }
  }, []);

  // Validar se a URL parece ser válida
  const validatePdfUrl = useCallback((url, filename) => {
    if (!url || typeof url !== 'string') {
      return false;
    }

    // URLs muito curtas provavelmente são inválidas
    if (url.length < 5) {
      return false;
    }

    // Verificar se tem caracteres suspeitos
    if (url.includes('undefined') || url.includes('null')) {
      return false;
    }

    return true;
  }, []);

  // Fazer download do arquivo (PDF, TXT, etc.) via fetch+blob para funcionar cross-origin
  const downloadPdf = useCallback(async (url, filename = 'documento.pdf') => {
    if (!url) {
      console.error('❌ URL não fornecida para download');
      alert('Erro: Não é possível fazer download, URL não encontrada');
      return;
    }

    try {
      const processedUrl = processUrl(url);

      // Extrair filename da URL se o default foi passado
      if (filename === 'documento.pdf' && processedUrl) {
        try {
          const urlFilename = decodeURIComponent(processedUrl.split('/').pop().split('?')[0]);
          if (urlFilename && urlFilename.includes('.')) {
            filename = urlFilename;
          }
        } catch (_) { /* manter default */ }
      }

      // Usar fetch + blob para forçar download (funciona cross-origin)
      const response = await fetch(processedUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Liberar memória do blob após breve delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('❌ Erro ao fazer download via blob, tentando fallback:', error);

      // Fallback: link direto com download attribute
      try {
        const processedUrl = processUrl(url);
        const link = document.createElement('a');
        link.href = processedUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        console.error('❌ Erro no fallback:', fallbackError);
        alert('Erro ao fazer download do arquivo');
      }
    }
  }, [processUrl]);

  // Extrair informações de um arquivo PDF de uma mensagem
  const extractPdfInfoFromMessage = useCallback((message, companyId = null) => {
    // ✅ CORRIGIDO: O mediaUrl já vem do backend com a URL completa construída pelo modelo Message
    // O modelo Message constrói: ${BACKEND_URL}/public/company${companyId}/flow/${fileName}
    // Então não precisamos fazer transformações, apenas usar o mediaUrl diretamente
    let mediaUrl = message.mediaUrl || '';
    
    // Se mediaUrl já é uma URL completa (começa com http), usar diretamente
    // Se não, o processUrl vai construir corretamente
    const processedUrl = mediaUrl.startsWith('http') ? mediaUrl : processUrl(mediaUrl);
    
    const info = {
      url: processedUrl,
      filename: 'documento.pdf',
      size: message.fileSize || null,
      mediaType: message.mediaType || '',
      isPdf: false
    };

    const resolvedName = getDocumentDownloadFilename(message, info.filename);
    if (resolvedName && resolvedName !== "arquivo") {
      info.filename = resolvedName;
    }

    // Extrair filename da URL se não obtido do body
    if (info.filename === 'documento.pdf' && info.url) {
      try {
        const urlParts = info.url.split('/');
        const urlFilename = urlParts[urlParts.length - 1];
        
        // Remover parâmetros de query se houver
        const cleanFilename = urlFilename.split("?")[0];
        const decodedFilename = stripStorageFilenamePrefix(
          decodeURIComponent(cleanFilename)
        );

        if (decodedFilename && decodedFilename.includes(".")) {
          info.filename = decodedFilename;
        }
      } catch (error) {
        console.warn('⚠️ Erro ao extrair filename da URL:', error);
      }
    }

    // Verificar se é PDF
    info.isPdf = isPdfUrl(info.url, info.filename, info.mediaType);

    return info;
  }, [processUrl]);

  return {
    // Ações principais
    downloadPdf,
    
    // Utilitários
    isPdfUrl,
    getDocumentDownloadFilename,
    isAttachmentDocumentMessage,
    isMediaAudioMessage,
    stripStorageFilenamePrefix,
    validatePdfUrl,
    extractPdfInfoFromMessage,
    processUrl,
  };
};